// src/api/features/modules/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createModule,
  updateModule,
  deleteModule,
  bulkDeleteModules,
} from "./api";

import type { Module, PaginatedModules, ModuleDTO } from "./types";
import { ModulesKeys } from "./types";

/* =========================================
 * MODULES — Write Hooks (Mutations Only)
 * ========================================= */

/** 🔹 Create module */
export function useCreateModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ModuleDTO.Create) => createModule(payload),
    onSuccess: (created) => {
      // Prime detail + refresh lists
      qc.setQueryData(ModulesKeys.detail(created.id), created);
      qc.invalidateQueries({ queryKey: ModulesKeys.all });
    },
  });
}

/** 🔹 Update module (optimistic detail + list patch) */
export function useUpdateModule(id: number) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: ModuleDTO.Update) => updateModule(id, payload),

    async onMutate(payload) {
      // Cancel outgoing queries
      await Promise.all([
        qc.cancelQueries({ queryKey: ModulesKeys.detail(id) }),
        qc.cancelQueries({ queryKey: ModulesKeys.all }),
      ]);

      const prevDetail = qc.getQueryData<Module>(ModulesKeys.detail(id));

      const prevLists = qc
        .getQueryCache()
        .findAll({ queryKey: ModulesKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedModules | undefined,
        }));

      // Optimistic detail patch
      if (prevDetail) {
        qc.setQueryData<Module>(ModulesKeys.detail(id), {
          ...prevDetail,
          ...payload,
        });
      }

      // Optimistic patch in lists
      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;

        const items = data.items.map((m) =>
          m.id === id ? ({ ...m, ...payload } as Module) : m
        );

        qc.setQueryData(snap.key, { ...data, items });
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _payload, ctx) {
      if (!ctx) return;

      // Rollback detail
      if (ctx.prevDetail) {
        qc.setQueryData(ModulesKeys.detail(id), ctx.prevDetail);
      }

      // Rollback lists
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: ModulesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ModulesKeys.all });
    },
  });
}

/** 🔹 Delete module (optimistic remove) */
export function useDeleteModule() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteModule(id),

    async onMutate(id) {
      await qc.cancelQueries({ queryKey: ModulesKeys.all });

      const prevLists = qc
        .getQueryCache()
        .findAll({ queryKey: ModulesKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedModules | undefined,
        }));

      // Optimistic remove from lists
      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;

        qc.setQueryData(snap.key, {
          ...data,
          items: data.items.filter((m) => m.id !== id),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }

      // Remove detail cache
      qc.removeQueries({ queryKey: ModulesKeys.detail(id) });

      return { prevLists };
    },

    onError(_err, _id, ctx) {
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: ModulesKeys.all });
    },
  });
}

/** 🔹 Bulk delete modules
 *  We keep this simple: no optimistic per-row removal, just invalidate lists.
 */
export function useBulkDeleteModules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => bulkDeleteModules(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ModulesKeys.all });
    },
  });
}
